import CloseIcon from "@mui/icons-material/CloseTwoTone";
import { Box, Popover, TextField, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SubmittedFeedbackSVG from "../../../../../../assets/images/svgs/SubmittedFeedback.svg";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaCheckbox from "../../../../../../components/common/BriaCheckbox/BriaCheckbox.tsx";
import styles from "./ImageNegativeFeedbackCard.module.scss";

type Props = {
	open: boolean;
	anchorEl: HTMLElement | null;
	value: string;
	onChange: (value: string) => void;
	selectedIssues: string[];
	onToggleIssue: (issueKey: string) => void;
	onClose: (e: any) => void;
	onSubmit: (e: any) => void;
	loading?: boolean;
	submitted?: boolean;
};

const ImageNegativeFeedbackCard = ({
	open,
	anchorEl,
	value,
	onChange,
	selectedIssues,
	onToggleIssue,
	onClose,
	onSubmit,
	loading,
	submitted,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.imageOverlay.disLike" });

	const issues = useMemo(
		() => [
			{ key: "poorPromptAdherence", label: t("issues.poorPromptAdherence") },
			{ key: "notAesthetic", label: t("issues.notAesthetic") },
			{ key: "notMatchingStyle", label: t("issues.notMatchingStyle") },
			{ key: "aiArtifacts", label: t("issues.aiArtifacts") },
		],
		[t],
	);

	const handlePaperWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		const isTextArea = (e.target as HTMLElement)?.closest("textarea");
		if (isTextArea) return;
		const scroller = document.scrollingElement || document.documentElement;
		scroller.scrollTop += e.deltaY;
		e.preventDefault();
	};

	return (
		<Popover
			open={open}
			onClose={onClose}
			anchorEl={anchorEl}
			anchorOrigin={{ vertical: "top", horizontal: "left" }}
			transformOrigin={{ vertical: "bottom", horizontal: "left" }}
			slotProps={{ paper: { className: styles.card, onWheel: handlePaperWheel } as any }}
		>
			{!submitted ? (
				<Box onMouseDown={(e) => e.stopPropagation()}>
					<CloseIcon className={styles.closeIcon} onClick={onClose} />
					<Box className={styles.issues}>
						<Typography className={styles.title}>{t("issues.title")}</Typography>
						{issues.map((issue) => {
							const checked = selectedIssues.includes(issue.key);
							return (
								<Box key={issue.key} onClick={() => onToggleIssue(issue.key)}>
									<BriaCheckbox
										checked={checked}
										onChange={() => onToggleIssue(issue.key)}
										inputProps={{ "aria-label": issue.label }}
									/>
									<Typography component="span" className={styles.issueText}>
										{issue.label}
									</Typography>
								</Box>
							);
						})}
					</Box>
					<Typography className={styles.label}>{t("negativeFeedbackLabel")}</Typography>
					<TextField
						className={styles.textArea}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						placeholder={t("placeholder")}
						multiline
						minRows={2}
						maxRows={3}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !loading) {
								const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
								const isWindowsOrLinux = !isMac;

								if ((isMac && e.metaKey) || (isWindowsOrLinux && e.ctrlKey)) {
									e.preventDefault();
									onSubmit(e);
								}
							}
						}}
						onClick={(e) => e.stopPropagation()}
						autoComplete="off"
					/>
					<Box className={styles.actions}>
						<BriaButton
							className={styles.sendButton}
							buttonType="primary"
							disabled={loading}
							onClick={onSubmit}
						>
							{t("button")}
						</BriaButton>
					</Box>
				</Box>
			) : (
				<Box className={styles.submitted}>
					<CloseIcon className={styles.closeIcon} onClick={onClose} />
					<img className={styles.submittedIcon} src={SubmittedFeedbackSVG} />
					<Typography className={styles.submittedText}>{t("feedback1")}</Typography>
					<Typography className={styles.submittedText}>{t("feedback2")}</Typography>
				</Box>
			)}
		</Popover>
	);
};

export default observer(ImageNegativeFeedbackCard);
